// frontend/src/components/Diario/TarefaCardMinimal.js

import React, { useState } from 'react';
import { 
    Clock, 
    CheckCircle, 
    AlertTriangle, 
    Pill, 
    Heart, 
    Sparkles, 
    UtensilsCrossed, 
    Move3D, 
    Dumbbell, 
    FileText, 
    Activity,
    RotateCcw,
    Info, 
    Trash2,
    Scale,
    Droplet
} from 'lucide-react';
import { parseLocalDateTime } from '../../utils/formatters';

// Mapeia o tipo de tarefa para um ícone e cor
const getTaskIcon = (tipo) => {
    const tipoLower = tipo?.toLowerCase() || '';
    
    // Medicação
    if (tipoLower.includes('medicação') || tipoLower.includes('medicacao')) {
        return { icon: Pill, color: 'text-blue-500 bg-blue-100' };
    }
    
    // Sinais Vitais
    if (tipoLower.includes('sinais vitais')) {
        return { icon: Heart, color: 'text-red-500 bg-red-100' };
    }
    
    // Higienização / Higiene
    if (tipoLower.includes('higienização') || tipoLower.includes('higienizacao') || tipoLower.includes('higiene')) {
        return { icon: Sparkles, color: 'text-indigo-500 bg-indigo-100' };
    }
    
    // Alimentação
    if (tipoLower.includes('alimentação') || tipoLower.includes('alimentacao')) {
        return { icon: UtensilsCrossed, color: 'text-amber-500 bg-amber-100' };
    }
    
    // Mudança de Decúbito / Posicionamento
    if (tipoLower.includes('mudança de decúbito') || tipoLower.includes('mudanca de decubito') || tipoLower.includes('posicionamento')) {
        return { icon: Move3D, color: 'text-teal-500 bg-teal-100' };
    }
    
    // Atividade Física
    if (tipoLower.includes('atividade física') || tipoLower.includes('atividade fisica')) {
        return { icon: Dumbbell, color: 'text-purple-500 bg-purple-100' };
    }
    
    // Pesagem
    if (tipoLower.includes('pesagem')) {
        return { icon: Scale, color: 'text-cyan-500 bg-cyan-100' };
    }
    
    // Glicemia
    if (tipoLower.includes('glicemia')) {
        return { icon: Droplet, color: 'text-pink-500 bg-pink-100' };
    }
    
    // Observação / Outro
    if (tipoLower.includes('observação') || tipoLower.includes('observacao') || tipoLower.includes('outro')) {
        return { icon: FileText, color: 'text-gray-500 bg-gray-100' };
    }
    
    // Padrão (caso não encontre nenhum tipo conhecido)
    return { icon: Activity, color: 'text-gray-500 bg-gray-100' };
};

/**
 * Modal para exibir detalhes da medicação
 */
const MedicacaoModal = ({ isOpen, onClose, tarefa }) => {
    if (!isOpen || !tarefa) return null;

    const { nome_medicamento, dose, unidade_medida, instrucoes_tarefa, plano_medico } = tarefa;
    const medicamentoInfo = tarefa.plano_medico || {};

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Detalhes da Medicação</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                    >
                        ×
                    </button>
                </div>
                
                <div className="space-y-3">
                    <div>
                        <span className="text-sm font-semibold text-gray-600">Medicamento:</span>
                        <p className="text-lg text-gray-900">{nome_medicamento || medicamentoInfo.nome_medicamento || 'Não informado'}</p>
                    </div>
                    
                    <div>
                        <span className="text-sm font-semibold text-gray-600">Dosagem:</span>
                        <p className="text-lg text-gray-900">
                            {dose || medicamentoInfo.dose || 'N/A'} {unidade_medida || medicamentoInfo.unidade_medida || ''}
                        </p>
                    </div>
                    
                    {(instrucoes_tarefa || medicamentoInfo.instrucoes) && (
                        <div>
                            <span className="text-sm font-semibold text-gray-600">Instruções:</span>
                            <p className="text-sm text-gray-700">{instrucoes_tarefa || medicamentoInfo.instrucoes}</p>
                        </div>
                    )}
                    
                    <div>
                        <span className="text-sm font-semibold text-gray-600">Horário Agendado:</span>
                        <p className="text-sm text-gray-700">
                            {tarefa.hora_agendada ? (() => {
                                const date = parseLocalDateTime(tarefa.hora_agendada);
                                return date ? date.toLocaleString('pt-BR') : 'N/A';
                            })() : 'N/A'}
                        </p>
                    </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * @param {object} tarefa - Objeto da tarefa pendente.
 * @param {function} onConcluir - Handler para concluir a tarefa.
 * @param {function} onExcluir - Handler para excluir a tarefa (opcional).
 */
const TarefaCardMinimal = ({ tarefa, onConcluir, onExcluir }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { id, tipo_tarefa, descricao, hora_agendada, status, nome_medicamento, dose, unidade_medida } = tarefa;
    const { icon: Icon, color } = getTaskIcon(tipo_tarefa);

    // Formata a hora para exibição (ex: 10:30:00 -> 10:30)
    // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
    const horaAgendadaParsed = hora_agendada ? parseLocalDateTime(hora_agendada) : null;
    const hora = horaAgendadaParsed ? horaAgendadaParsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A';
    
    // CORREÇÃO: Verifica se a tarefa está atrasada considerando o horário completo (não apenas a data)
    // Uma tarefa está atrasada APENAS se o horário agendado já passou (com margem de 15 minutos)
    // Não confiamos apenas no status do banco, pois pode ter sido marcado incorretamente
    const agora = new Date();
    const horaAgendada = horaAgendadaParsed;
    
    // Verifica se realmente está atrasada baseado no horário, não apenas no status
    let isAtrasada = false;
    if (horaAgendada) {
        const diferencaMinutos = (agora - horaAgendada) / (1000 * 60); // Diferença em minutos
        // Só considera atrasada se passou mais de 15 minutos do horário agendado
        isAtrasada = diferencaMinutos > 15;
        
        // Debug temporário (pode remover depois)
        if (process.env.NODE_ENV === 'development' && Math.abs(diferencaMinutos) < 120) {
            console.log(`[DEBUG Tarefa ${id}]`, {
                horaAgendada: horaAgendada.toLocaleString('pt-BR'),
                agora: agora.toLocaleString('pt-BR'),
                diferencaMinutos: diferencaMinutos.toFixed(2),
                isAtrasada,
                status
            });
        }
    } else if (status === 'Atrasada') {
        // Se não tem horário mas o status é Atrasada, mantém como atrasada
        isAtrasada = true;
    }
    
    // Verifica se é uma tarefa de medicação para exibir informações adicionais
    const isMedicacao = tipo_tarefa === 'Medicação';
    const temInfoMedicacao = nome_medicamento || dose;

    // Para tarefas de medicação, incluir o nome do medicamento na descrição se disponível
    let descricaoExibida = descricao || "Sem descrição.";
    if (isMedicacao && nome_medicamento && descricao) {
        // Se a descrição não contém o nome do medicamento, adicionar
        if (!descricao.includes(nome_medicamento)) {
            // Verificar se é o formato antigo "Administração de Medicamento (Plano X)"
            if (descricao.includes('Administração de Medicamento')) {
                descricaoExibida = descricao.replace('Administração de Medicamento', `Administração de ${nome_medicamento}`);
            } else {
                // Se não for o formato esperado, adicionar o nome do medicamento no início
                descricaoExibida = `${nome_medicamento} - ${descricao}`;
            }
        }
    }

    return (
        <div className={`
            flex justify-between items-start p-4 mb-3 rounded-xl transition 
            ${isAtrasada ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200'}
            shadow-sm hover:shadow-md border
        `}>
            {/* Ícone e Detalhes da Tarefa */}
            <div className="flex items-start flex-grow pr-4">
                <div className={`p-2 rounded-full mr-3 shrink-0 ${color}`}>
                    <Icon size={20} />
                </div>
                <div className="flex-1">
                    <div className="flex items-center text-sm font-semibold mb-1">
                        <span className="text-gray-700">{tipo_tarefa}</span>
                        {isAtrasada && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium text-red-800 bg-red-200 rounded-full flex items-center">
                                <AlertTriangle size={12} className="mr-1" /> Atrasada
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-gray-500 leading-snug mb-1">{descricaoExibida}</p>
                    
                    {/* Informações de Medicação no Card */}
                    {isMedicacao && temInfoMedicacao && (
                        <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="text-xs font-semibold text-blue-800 mb-1">
                                        {nome_medicamento || 'Medicamento'}
                                    </p>
                                    <p className="text-xs text-blue-700">
                                        Dosagem: <span className="font-semibold">{dose || 'N/A'} {unidade_medida || ''}</span>
                                    </p>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsModalOpen(true);
                                    }}
                                    className="ml-2 p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full transition"
                                    title="Ver detalhes da medicação"
                                >
                                    <Info size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Hora e Ações */}
            <div className="flex flex-col items-end shrink-0">
                <div className="flex items-center text-sm font-bold text-gray-600 mb-2">
                    <Clock size={16} className="mr-1 text-gray-400" />
                    {hora}
                </div>
                
                {/* Botões de Ação */}
                <div className="flex gap-2 w-full">
                    {/* Botão de Excluir (se fornecido) */}
                    {onExcluir && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Tem certeza que deseja excluir a tarefa "${descricao || tipo_tarefa}"? Esta ação não pode ser desfeita.`)) {
                                    onExcluir(tarefa);
                                }
                            }}
                            className={`
                                flex items-center justify-center
                                bg-red-500 text-white font-semibold text-xs
                                py-2 px-3 rounded-lg shadow-md hover:bg-red-600 transition
                                focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50
                                flex-1
                            `}
                            aria-label={`Excluir tarefa ${id}`}
                            title="Excluir tarefa"
                        >
                            <Trash2 size={14} />
                        </button>
                    )}
                    
                    {/* Botão de Concluir */}
                    <button
                        onClick={() => onConcluir(tarefa)}
                        className={`
                            flex items-center justify-center
                            bg-emerald-500 text-white font-semibold text-xs
                            py-2 px-3 rounded-lg shadow-md hover:bg-emerald-600 transition
                            focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50
                            ${onExcluir ? 'flex-1' : 'w-full'}
                        `}
                        aria-label={`Concluir tarefa ${id}`}
                    >
                        <CheckCircle size={16} className={onExcluir ? '' : 'mr-1'} />
                        {!onExcluir && 'Concluir'}
                    </button>
                </div>
            </div>
            
            {/* Modal de Detalhes da Medicação */}
            <MedicacaoModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                tarefa={tarefa} 
            />
        </div>
    );
};

export default TarefaCardMinimal;